// Safe camera/microphone preference storage. Origin-scoped, opaque device
// IDs only — never labels, permission state, media bytes, or a screen/window
// identifier (browsers deliberately prevent persisting that choice).

const KEY = 'clip.capture-preferences.v1';
const DISPLAY_MODES = ['screen', 'window', 'browser'] as const;
export type DisplayModePreference = (typeof DISPLAY_MODES)[number];
const CAPTURE_MODES = ['screen', 'camera', 'both'] as const;
export type CaptureModePreference = (typeof CAPTURE_MODES)[number];
const CAMERA_FACINGS = ['user', 'environment'] as const;
export type CameraFacingPreference = (typeof CAMERA_FACINGS)[number];

export interface CapturePreferences {
  captureMode: CaptureModePreference;
  cameraEnabled: boolean;
  microphoneEnabled: boolean;
  displayModePreference?: DisplayModePreference;
  cameraDeviceId?: string;
  cameraFacing?: CameraFacingPreference;
  microphoneDeviceId?: string;
}

const DEFAULTS: CapturePreferences = {
  captureMode: 'screen',
  cameraEnabled: false,
  // Mic defaults ON: a silent recording is a much more common complaint
  // ("the audio didn't capture anything") than an unwanted hot mic, and
  // denial/no-device already falls back to screen-only gracefully.
  microphoneEnabled: true,
};

function isValid(v: unknown): v is CapturePreferences {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  if (!CAPTURE_MODES.includes(o.captureMode as CaptureModePreference)) return false;
  if (typeof o.cameraEnabled !== 'boolean') return false;
  if (typeof o.microphoneEnabled !== 'boolean') return false;
  if (o.displayModePreference !== undefined && !DISPLAY_MODES.includes(o.displayModePreference as DisplayModePreference)) return false;
  if (o.cameraDeviceId !== undefined && typeof o.cameraDeviceId !== 'string') return false;
  if (o.cameraFacing !== undefined && !CAMERA_FACINGS.includes(o.cameraFacing as CameraFacingPreference)) return false;
  if (o.microphoneDeviceId !== undefined && typeof o.microphoneDeviceId !== 'string') return false;
  return true;
}

/** Malformed/old-version data is discarded rather than migrated. */
export function loadPreferences(): CapturePreferences {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed: unknown = JSON.parse(raw);
    return isValid(parsed) ? parsed : { ...DEFAULTS };
  } catch {
    return { ...DEFAULTS };
  }
}

/** A blocked/quota-exhausted store just means no restored preference next time. */
export function savePreferences(prefs: CapturePreferences): boolean {
  try {
    localStorage.setItem(KEY, JSON.stringify(prefs));
    return true;
  } catch {
    return false;
  }
}

export interface DeviceOption {
  deviceId: string;
  label: string;
}

/** Chrome appends a raw USB vendor:product id in parens to some device
 * labels (e.g. "Anker PowerConf C200 (291a:3369)") — meaningless to a
 * creator picking a mic, so it's stripped for display. */
function cleanLabel(label: string): string {
  return label.replace(/\s*\([0-9a-f]{4}:[0-9a-f]{4}\)\s*$/i, '').trim();
}

export async function listDevices(kind: 'videoinput' | 'audioinput'): Promise<DeviceOption[]> {
  const all = await navigator.mediaDevices.enumerateDevices();
  return all
    .filter((d) => d.kind === kind && d.deviceId)
    .map((d, i) => ({
      deviceId: d.deviceId,
      label: d.label ? cleanLabel(d.label) : `${kind === 'videoinput' ? 'Camera' : 'Microphone'} ${i + 1}`,
    }));
}

/** True once device labels are populated — i.e. permission has been granted at least once. */
export function hasDeviceLabels(devices: DeviceOption[]): boolean {
  return devices.some((d) => !/^(Camera|Microphone) \d+$/.test(d.label));
}
